import React, { useState } from 'react';
import { Users, Plus, Upload, ArrowRight } from 'lucide-react';
import { Button } from '../../components/ui/button';
import ClientTable from '../../features/clients/components/ClientTable';
import { useClients } from '../../features/clients/hooks/useClients';
import AddClientModal from '../../components/clients/AddClientModal';
import CsvImportModal from '../../components/leads/CsvImportModal';

/**
 * ClientsPage
 *
 * Home for existing customers. Primary onboarding surface — operators
 * migrating from another CRM bring their book of business here via
 * "Migrate Customers" (the same modal used for recurring program-bound
 * activations; the Service Program is the source of truth for everything
 * the importer doesn't ask about).
 *
 * Empty state actively guides first-time owners through their two
 * options: add one client manually, or migrate the whole book in one CSV.
 */
export default function ClientsPage() {
  const {
    clients, loading, error,
    searchTerm, setSearchTerm, isSearching,
    hasMore, loadMore, loadingMore,
  } = useClients();
  const [showAddClient,      setShowAddClient]      = useState(false);
  const [showMigrateClients, setShowMigrateClients] = useState(false);

  const hasClients = !loading && clients && clients.length > 0;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Users className="h-8 w-8 text-primary" />
          <div>
            <h1 className="text-3xl font-bold text-secondary">Clients</h1>
            <p className="text-muted-foreground">
              Your existing customers and active service plans
            </p>
          </div>
        </div>

        {/* Top-right action group — only shown once the operator has at least
            one client. Empty state below carries the primary CTAs for new
            companies. */}
        {hasClients && (
          <div className="flex items-center gap-2">
            <Button
              onClick={() => setShowMigrateClients(true)}
              variant="outline"
              className="shadow-sm"
              data-testid="clients-migrate-btn"
            >
              <Upload className="h-4 w-4 mr-2" />
              Migrate Customers
            </Button>
            <Button
              onClick={() => setShowAddClient(true)}
              className="text-white shadow-sm"
              style={{ backgroundColor: 'var(--cb-orange)' }}
              data-testid="clients-add-client-btn"
            >
              <Plus className="h-4 w-4 mr-2" />
              Add Client
            </Button>
          </div>
        )}
      </div>

      {/* Empty state — first impression for new companies. Doubles as the
          primary onboarding pathway: every CleanBid customer is expected
          to land here on day 1 with zero clients. */}
      {!loading && !error && (!clients || clients.length === 0) ? (
        <div
          className="rounded-2xl border border-dashed border-gray-300 bg-white px-6 py-14 sm:py-20 text-center max-w-3xl mx-auto"
          data-testid="clients-empty-state"
        >
          <div className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-orange-50 mb-5">
            <Users className="w-7 h-7" style={{ color: 'var(--cb-orange)' }} />
          </div>
          <h2 className="text-2xl font-bold mb-2" style={{ color: 'var(--cb-navy)' }}>
            No clients yet?
          </h2>
          <p className="text-sm text-gray-500 max-w-md mx-auto mb-7 leading-relaxed">
            Create your first client manually or migrate your existing customers from another CRM.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
            <Button
              onClick={() => setShowAddClient(true)}
              variant="outline"
              size="lg"
              className="shadow-sm w-full sm:w-auto"
              data-testid="clients-empty-add-client-btn"
            >
              <Plus className="h-4 w-4 mr-2" />
              Add Client
            </Button>
            <Button
              onClick={() => setShowMigrateClients(true)}
              size="lg"
              className="text-white shadow-sm w-full sm:w-auto"
              style={{ backgroundColor: 'var(--cb-orange)' }}
              data-testid="clients-empty-migrate-btn"
            >
              <Upload className="h-4 w-4 mr-2" />
              Migrate Customers
              <ArrowRight className="h-4 w-4 ml-2" />
            </Button>
          </div>
          <p className="text-[11px] text-gray-400 mt-6 max-w-sm mx-auto leading-relaxed">
            Migrating recurring customers? Set up your Service Programs first
            in <span className="font-medium">Settings → Service Programs</span>{' '}
            so each customer can be assigned to one in the same upload.
          </p>
        </div>
      ) : (
        <ClientTable
          clients={clients}
          loading={loading}
          error={error}
          externalSearchTerm={searchTerm}
          onSearchTermChange={setSearchTerm}
          isSearching={isSearching}
          hasMore={hasMore}
          loadMore={loadMore}
          loadingMore={loadingMore}
        />
      )}

      {/* Modals */}
      <AddClientModal
        isOpen={showAddClient}
        onClose={() => setShowAddClient(false)}
      />
      <CsvImportModal
        isOpen={showMigrateClients}
        onClose={() => setShowMigrateClients(false)}
        mode="migration"
      />
    </div>
  );
}
